import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { Bell, MoreHorizontal, Building2, Briefcase, Shield } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Logo } from "@/components/brand/Logo";
import { useSession } from "@/context/Session";
import { fdate } from "@/lib/format";

function Notifications() {
  const { api } = useSession();
  const [items, setItems] = useState([]);
  const load = () => api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []); // eslint-disable-line
  const unread = items.filter((n) => !n.read).length;
  return (
    <Popover onOpenChange={(o) => { if (!o && unread) api.post("/notifications/read").then(load); }}>
      <PopoverTrigger data-testid="notifications-btn" className="relative rounded-xl p-2 hover:bg-[#E3FAF7]" aria-label="Notificações">
        <Bell className="h-5 w-5 text-[#12343B]" />
        {unread > 0 && <span data-testid="notifications-count" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#00FF40] px-1 text-[10px] font-bold text-[#12343B]">{unread}</span>}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <p className="border-b px-4 py-3 text-sm font-bold">Notificações</p>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 && <p className="p-4 text-sm text-[#587178]">Nada por aqui.</p>}
          {items.map((n) => <div key={n.id} className={`border-b px-4 py-3 text-sm ${n.read ? "" : "bg-[#E3FAF7]/60"}`}><p className="font-semibold">{n.title}</p>{n.body && <p className="text-[#587178]">{n.body}</p>}<p className="mt-1 text-xs text-[#587178]">{fdate(n.created_at)}</p></div>)}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function CompanySwitcher({ side }) {
  const { me, companyId, setCompanyId } = useSession();
  const list = (me?.companies || []).filter((c) => c.side === side);
  if (!list.length) return null;
  return (
    <select data-testid="company-switcher" value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="field h-10 max-w-[180px] sm:max-w-[260px]">
      <option value="">Selecione a empresa</option>
      {list.map((c) => <option key={c.id + side} value={c.id}>{c.name}{c.is_demo ? " (demo)" : ""}</option>)}
    </select>
  );
}

function PanelLinks() {
  const { me } = useSession();
  const links = [];
  if (me?.companies?.some((c) => c.side === "empresa")) links.push(["/app", "Empresário", Building2]);
  if (me?.orgs?.length) links.push(["/contador", "Contador", Briefcase]);
  if (me?.user?.platform_role) links.push(["/admin", "Admin", Shield]);
  if (links.length < 2) return null;
  return <div className="flex gap-1 rounded-xl bg-[#F4F6F8] p-1">{links.map(([to, l, I]) => (
    <NavLink key={to} to={to} end={false} data-testid={`panel-link-${l.toLowerCase()}`} className={({ isActive }) => `flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold ${isActive ? "bg-white shadow-sm text-[#12343B]" : "text-[#587178]"}`}><I className="h-3.5 w-3.5" /><span className="hidden sm:inline">{l}</span></NavLink>))}</div>;
}

const Item = ({ to, label, icon: Icon, end, onClick }) => (
  <NavLink to={to} end={end} onClick={onClick} data-testid={`menu-${to.split("/").filter(Boolean).join("-")}`}
    className={({ isActive }) => `flex items-center gap-3 rounded-[10px] px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[#E3FAF7] text-[#12343B] font-semibold" : "text-[#587178] hover:bg-[#F4F6F8]"}`}>
    <Icon className="h-[18px] w-[18px]" />{label}
  </NavLink>
);

export default function AppShell({ menu, side, title, needsCompany }) {
  const { companyId, me } = useSession();
  const [more, setMore] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();
  const root = menu[0].to;
  const companyOk = !needsCompany || (companyId && me?.companies?.some((c) => c.id === companyId && c.side === side));
  const companyFree = [root, `${root}/empresas`, `${root}/configuracoes`];
  const blocked = needsCompany && !companyOk && !companyFree.includes(loc.pathname);
  return (
    <div className="min-h-screen bg-[#F4F6F8]">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-[#DFE6E9] bg-white lg:flex">
        <div className="flex h-16 items-center gap-2 px-5"><Link to="/"><Logo /></Link><span className="rounded-md bg-[#E3FAF7] px-2 py-0.5 text-[10px] font-bold uppercase text-[#2a8f86]">{title}</span></div>
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 pb-6">{menu.map((m) => <Item key={m.to} {...m} end={m.to === root} />)}</nav>
      </aside>
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-2 border-b border-[#DFE6E9] bg-white/85 px-4 backdrop-blur-xl lg:px-8">
          <div className="flex items-center gap-2"><Link to="/" className="lg:hidden"><Logo size="text-xl" /></Link><CompanySwitcher side={side} /></div>
          <div className="flex items-center gap-2"><PanelLinks /><Notifications /><UserButton /></div>
        </header>
        <main className="mx-auto max-w-7xl px-4 pb-28 pt-6 lg:px-8 lg:pb-12">
          {blocked ? (
            <div className="card-soft p-8 text-center" data-testid="select-company-prompt">
              <p className="font-semibold text-[#12343B]">Selecione uma empresa no topo da tela para continuar.</p>
              {side === "contador" && <button className="btn-primary mt-4" onClick={() => nav("/contador/empresas")}>Ver empresas atendidas</button>}
            </div>
          ) : <Outlet key={companyId} />}
        </main>
      </div>
      <nav className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 border-t border-[#DFE6E9] bg-white/95 backdrop-blur-xl lg:hidden" data-testid="bottom-nav">
        {menu.slice(0, 4).map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} end={to === root} className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 text-[11px] font-semibold ${isActive ? "text-[#12343B]" : "text-[#587178]"}`}>
            {({ isActive }) => <><span className={`rounded-full px-4 py-1 ${isActive ? "bg-[#81D8D0]/40" : ""}`}><Icon className="h-5 w-5" /></span><span className="max-w-[72px] truncate">{label}</span></>}
          </NavLink>))}
        <Sheet open={more} onOpenChange={setMore}>
          <SheetTrigger data-testid="bottom-nav-more" className="flex flex-col items-center gap-1 py-2.5 text-[11px] font-semibold text-[#587178]"><span className="px-4 py-1"><MoreHorizontal className="h-5 w-5" /></span>Mais</SheetTrigger>
          <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto rounded-t-3xl">
            <SheetTitle className="mb-3">Menu</SheetTitle>
            <div className="grid grid-cols-2 gap-1">{menu.slice(4).map((m) => <Item key={m.to} {...m} onClick={() => setMore(false)} />)}</div>
          </SheetContent>
        </Sheet>
      </nav>
    </div>
  );
}
